/**
 * Help links used across the portal: who is viewing, opening the help panel, the "How this works" link and the
 * one-line hint in empty lists. Kept apart from Help.tsx so core components can use them.
 */
import { Icon } from "../../components/Icon";
import { BRAND } from "../../lib/config";
import { useHash } from "../../lib/route";
import { helpPage, pageForRoute, type Who } from "./help-data";

/* ------------------------------------------------------------------ who is viewing, and the panel's state */

export let viewer: { who: Who; role: string } = { who: "office", role: "admin" };
/** Set once by the app shell: crew users see crew and all pages. */
export function setHelpViewer(role: string | undefined) {
  viewer = { who: role === "controller" ? "crew" : "office", role: role ?? "admin" };
}
export const isDirector = () => viewer.role === "director";

export type PanelState = { kind: "page"; id: string } | { kind: "report" } | null;
export const listeners = new Set<(s: PanelState) => void>();
const show = (s: PanelState) => listeners.forEach((l) => l(s));
export const openHelp = (id: string) => show({ kind: "page", id });
export const openReport = () => show({ kind: "report" });
export const reportsOn = () => Boolean(BRAND.support);

/** The portal screen the user is on ("#/jobs" for "#/jobs/123"); crew app screens are "#/my". */
export function currentRoute(): string {
  if (viewer.who === "crew") return "#/my";
  const base = `#/${window.location.hash.replace(/^#\/?/, "").split("/")[0] ?? ""}`;
  return base === "#/" ? "#/today" : base;
}

/* ------------------------------------------------------------------ links */

/** "How this works" for the current screen (or a given page), when the client layer has a guide for it. */
export function HowThisWorks({ route, pageId, label = "How this works", compact }: { route?: string; pageId?: string; label?: string; compact?: boolean }) {
  useHash();
  const page = pageId ? helpPage(pageId, viewer.who) : pageForRoute(route ?? currentRoute(), viewer.who);
  if (!page) return null;
  return (
    <button type="button" className={compact ? "helplink helplink--compact" : "btn btn--outline btn--sm helplink"} onClick={() => openHelp(page.id)}>
      <Icon name="help" size={compact ? 16 : 18} /> {label}
    </button>
  );
}

/** One line from the screen's guide under an empty list. */
export function HelpHint({ route, className }: { route?: string; className?: string }) {
  const page = pageForRoute(route ?? currentRoute(), viewer.who);
  if (!page) return null;
  return (
    <p className={className ? `helphint ${className}` : "helphint"}>
      {page.summary}{" "}
      <button type="button" className="linkbtn" onClick={() => openHelp(page.id)}>Read the guide</button>
    </p>
  );
}

